import { Link } from "react-router-dom";

function Section({ id, title, children }) {
  return (
    <section id={id} className="mb-8">
      <h2 className="text-xl md:text-2xl font-bold text-text-primary mb-3">{title}</h2>
      <div className="text-text-secondary text-sm leading-relaxed space-y-2">{children}</div>
    </section>
  );
}

function Kbd({ children }) {
  return (
    <kbd className="px-1.5 py-0.5 mx-0.5 rounded bg-bg-secondary border border-bg-secondary text-text-primary text-xs font-mono">
      {children}
    </kbd>
  );
}

export default function ScorerGuide() {
  return (
    <div className="min-h-screen bg-bg-main py-10 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <Link to="/dashboard" className="text-text-secondary hover:text-text-primary text-sm">
            ← Back to dashboard
          </Link>
          <h1 className="text-3xl md:text-4xl font-bold text-text-primary mt-2">Scorer guide</h1>
          <p className="text-text-secondary text-sm mt-1">
            A practical reference for the team scorers. Sections in the order you'll need them during a match.
          </p>
        </div>

        {/* TOC */}
        <nav className="mb-8 bg-bg-primary border border-bg-secondary rounded-xl p-4">
          <p className="text-xs uppercase tracking-wider text-text-secondary mb-2">On this page</p>
          <ol className="grid grid-cols-1 md:grid-cols-2 gap-1 text-sm list-decimal list-inside">
            <li><a href="#login" className="text-emerald-400 hover:underline">Logging in</a></li>
            <li><a href="#console" className="text-emerald-400 hover:underline">Reaching the scoring console</a></li>
            <li><a href="#layout" className="text-emerald-400 hover:underline">The console at a glance</a></li>
            <li><a href="#flow" className="text-emerald-400 hover:underline">Scoring a ball</a></li>
            <li><a href="#extras" className="text-emerald-400 hover:underline">Extras</a></li>
            <li><a href="#wicket" className="text-emerald-400 hover:underline">Recording a wicket</a></li>
            <li><a href="#edit" className="text-emerald-400 hover:underline">Editing a past ball</a></li>
            <li><a href="#undo" className="text-emerald-400 hover:underline">Undoing the last ball</a></li>
            <li><a href="#innings" className="text-emerald-400 hover:underline">Innings transitions</a></li>
            <li><a href="#dls" className="text-emerald-400 hover:underline">Applying DLS</a></li>
            <li><a href="#shortcuts" className="text-emerald-400 hover:underline">Keyboard shortcuts</a></li>
            <li><a href="#recovery" className="text-emerald-400 hover:underline">Recovery from mistakes</a></li>
            <li><a href="#after" className="text-emerald-400 hover:underline">After the match</a></li>
            <li><a href="#ai" className="text-emerald-400 hover:underline">AI features &amp; Zencredits</a></li>
          </ol>
        </nav>

        <Section id="login" title="1. Logging in">
          <ol className="list-decimal list-inside space-y-1">
            <li>Go to <span className="text-text-primary">zencricketdata.com/login</span></li>
            <li>Enter your username (usually your email) and password</li>
            <li>Forgot it? Click <span className="text-text-primary">"Forgot password?"</span> and check your inbox.</li>
          </ol>
          <p>If your account was just created and you got a temporary password, log in once and immediately reset it.</p>
        </Section>

        <Section id="console" title="2. Reaching the scoring console">
          <ol className="list-decimal list-inside space-y-1">
            <li>Land on the <span className="text-text-primary">Dashboard</span> after login.</li>
            <li>Click <span className="text-text-primary">"Score &amp; manage matches"</span> (or go to <code>/cricket/matches</code>).</li>
            <li>Find your match in the list and click into it.</li>
            <li>Match prep flows: <span className="text-text-primary">Toss → Playing XI → Confirm scoring</span>.</li>
            <li>Click <span className="text-text-primary">"Start ball-by-ball scoring"</span> — opens the console at <code>/ball/scoring/&lt;id&gt;</code>.</li>
          </ol>
        </Section>

        <Section id="layout" title="3. The scoring console at a glance">
          <p>Three columns, plus a sticky header at the top.</p>
          <ul className="list-disc list-inside space-y-1">
            <li><strong className="text-text-primary">Left pane:</strong> video player, recent balls + partnership, run buttons, extras, wicket / DRS, batter / bowler tables, score, edit / undo</li>
            <li><strong className="text-text-primary">Centre pane:</strong> Pitch map → Height impact → Wagon wheel pickers (auto-progress), New / Repl / WK buttons, commentary feed</li>
            <li><strong className="text-text-primary">Right pane:</strong> Delivery type, Stroke, Foot movement, Connection, Keeper — revealed progressively as you fill them</li>
          </ul>
        </Section>

        <Section id="flow" title="4. Scoring a ball — the standard flow">
          <ol className="list-decimal list-inside space-y-1">
            <li><strong className="text-text-primary">Pick runs</strong> — click 0 / 1 / 2 / 3 / 4 / 5 / 6 in the run-buttons grid (left pane)</li>
            <li>
              <strong className="text-text-primary">Mark spatial</strong> — centre pane has 3 tabs that auto-progress:
              <ul className="list-disc list-inside ml-4 mt-1">
                <li><strong>Pitch map</strong> → click where the ball pitched</li>
                <li><strong>Height impact</strong> → click where it would have struck the batter</li>
                <li><strong>Wagon wheel</strong> → click where it went off the bat</li>
              </ul>
            </li>
            <li><strong className="text-text-primary">Optional tags</strong> in the right pane (delivery / stroke / footwork / connection / keeper) revealed one at a time as you select.</li>
            <li><strong className="text-text-primary">SAVE BALL</strong> — click the big red button (or press <Kbd>Enter</Kbd> / <Kbd>Space</Kbd> if shortcuts are on).</li>
          </ol>
          <p>The system auto-rotates strike on odd runs, auto-clears single-ball toggles (wide / no-ball / etc.), and chains free hit when previous ball was a no-ball.</p>
        </Section>

        <Section id="extras" title="5. Extras (the row above the wicket buttons)">
          <ul className="list-disc list-inside space-y-1">
            <li><strong className="text-text-primary">DB</strong> = Dead ball (no runs / no extras count)</li>
            <li><strong className="text-text-primary">Wd</strong> = Wide</li>
            <li><strong className="text-text-primary">NB</strong> = No ball — next ball auto-becomes a free hit</li>
            <li><strong className="text-text-primary">B</strong> = Bye (runs scored without bat contact)</li>
            <li><strong className="text-text-primary">LB</strong> = Leg bye (off the body)</li>
            <li><strong className="text-text-primary">SR</strong> = Short run (one fewer run counts)</li>
            <li><strong className="text-text-primary">QR</strong> = Quick running (informational)</li>
            <li><strong className="text-text-primary">FHit</strong> = Free hit (only RUN_OUT/MANKAD dismissals legal — system enforces)</li>
            <li><strong className="text-text-primary">Wk</strong> = Open wicket modal</li>
            <li><strong className="text-text-primary">DRS</strong> = Mark this ball as DRS-appealed</li>
          </ul>
        </Section>

        <Section id="wicket" title="6. Recording a wicket">
          <ol className="list-decimal list-inside space-y-1">
            <li>Click <strong className="text-text-primary">Wk</strong> to open the wicket modal</li>
            <li>Pick the wicket type: BOWLED, LBW, CAUGHT, RUN_OUT, STUMPED, HIT_WICKET, MANKAD, HIT_TWICE, OBSTRUCTING, RETIRED, TIMED_OUT</li>
            <li>Pick the dismissed batter (defaults to striker)</li>
            <li>For CAUGHT pick the fielder; STUMPED the keeper; RUN_OUT/MANKAD the fielder(s) involved</li>
            <li>Confirm + close the modal</li>
            <li>The "Wk" toggle is now active — click SAVE BALL as normal</li>
          </ol>
          <p>The bowler is auto-credited for BOWLED / LBW / CAUGHT / STUMPED / HIT_WICKET. RUN_OUT / RETIRED / etc. don't credit the bowler.</p>
        </Section>

        <Section id="edit" title="7. Editing a past ball">
          <p>Two paths:</p>
          <p>
            <strong className="text-text-primary">A.</strong> Click the commentary line in the centre pane — loads that ball into the main form for inline editing. Click UPDATE BALL to save.
          </p>
          <p>
            <strong className="text-text-primary">B.</strong> Click <strong className="text-text-primary">EDIT BALL</strong> (blue pill, bottom-left) — opens a full editor modal with 6 tabs (Outcome / Extras / Wicket / Players / Spatial / Commentary). Spatial tab uses the same graphical pickers as live scoring.
          </p>
        </Section>

        <Section id="undo" title="8. Undoing the last ball">
          <p>The <strong className="text-text-primary">UNDO BALL</strong> pill (amber, bottom-left) shows a 30-second countdown after each save.</p>
          <ul className="list-disc list-inside space-y-1">
            <li>Within 30s: click → undoes the last ball</li>
            <li>After 30s: button greys out — use <strong className="text-text-primary">EDIT BALL</strong> instead</li>
          </ul>
        </Section>

        <Section id="innings" title="9. Innings transitions">
          <p>When the innings ends (10 wickets, overs out, or target reached), the system auto-shows a closure-type prompt:</p>
          <ul className="list-disc list-inside space-y-1">
            <li><strong className="text-text-primary">All out / Overs completed / Target reached</strong> → end and start next innings</li>
            <li><strong className="text-text-primary">Declared (Test only)</strong> → persists <code>closure_type=DECLARED</code></li>
            <li><strong className="text-text-primary">Forfeited / Abandoned</strong> → match-result-specific options</li>
          </ul>
          <p>For Test cricket after innings 2 the system asks <strong className="text-text-primary">Enforce follow-on?</strong> if the lead is 200+ runs.</p>
          <p>For tied limited-overs matches, the system prompts <strong className="text-text-primary">Start super over?</strong> Boundary count decides if also tied.</p>
        </Section>

        <Section id="dls" title="10. Applying DLS (rain-affected ODI)">
          <ol className="list-decimal list-inside space-y-1">
            <li>Click <strong className="text-text-primary">MORE</strong> in the action bar</li>
            <li>Choose <strong className="text-text-primary">Apply DLS</strong></li>
            <li>Enter the revised target and revised max overs</li>
            <li>Chasing innings' aggregate updates with the new target</li>
          </ol>
        </Section>

        <Section id="shortcuts" title="11. Keyboard shortcuts (only when toggle is ON)">
          <p>The header bar has a <strong className="text-text-primary">⌨ ON / ⌨ OFF</strong> pill. Default is OFF to prevent accidental scoring. Click to enable.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
            <p><Kbd>0</Kbd>–<Kbd>6</Kbd> set runs</p>
            <p><Kbd>W</Kbd> toggle wide</p>
            <p><Kbd>N</Kbd> toggle no-ball</p>
            <p><Kbd>B</Kbd> toggle bye</p>
            <p><Kbd>L</Kbd> toggle leg-bye</p>
            <p><Kbd>K</Kbd> toggle wicket</p>
            <p><Kbd>U</Kbd> undo last ball</p>
            <p><Kbd>Enter</Kbd> / <Kbd>Space</Kbd> save ball</p>
            <p><Kbd>T</Kbd> cycle Pitch / Height / Wagon</p>
            <p><Kbd>Esc</Kbd> cancel / close modal</p>
            <p><Kbd>?</Kbd> shortcuts modal (always works)</p>
          </div>
        </Section>

        <Section id="recovery" title="12. Recovery from common mistakes">
          <table className="w-full text-sm border border-bg-secondary rounded-lg overflow-hidden">
            <thead className="bg-bg-primary">
              <tr><th className="text-left p-2 text-text-primary">Situation</th><th className="text-left p-2 text-text-primary">Fix</th></tr>
            </thead>
            <tbody className="divide-y divide-bg-secondary">
              <tr><td className="p-2">Saved wrong runs</td><td className="p-2">UNDO within 30s, or EDIT BALL</td></tr>
              <tr><td className="p-2">Wrong wicket type</td><td className="p-2">EDIT BALL → Wicket tab → change</td></tr>
              <tr><td className="p-2">Marked free hit by mistake</td><td className="p-2">Toggle FHit off; if saved, EDIT BALL → Extras → uncheck</td></tr>
              <tr><td className="p-2">Wrong striker / non-striker</td><td className="p-2">Re-pick from dropdown live; EDIT BALL → Players if saved</td></tr>
              <tr><td className="p-2">Match froze on tie / follow-on / DLS</td><td className="p-2">Refresh page (Ctrl+R) — saved balls persist</td></tr>
              <tr><td className="p-2">"Network error / queued" message</td><td className="p-2">Ball is in local queue, will sync. Don't refresh until "Synced"</td></tr>
            </tbody>
          </table>
        </Section>

        <Section id="after" title="13. After the match">
          <ol className="list-decimal list-inside space-y-1">
            <li>Click <strong className="text-text-primary">End match</strong> (action bar → MORE → end-match)</li>
            <li>Pick result type: DECISIVE / TIE / NO_RESULT / ABANDONED / DRAW (Tests)</li>
            <li>System finalises <code>Match.result_type</code>; public scorecard at <code>/cricket/matches/&lt;id&gt;/scorecard</code> becomes the final view.</li>
          </ol>
        </Section>

        <Section id="ai" title="14. AI features & Zencredits">
          <p>
            Zencredits are the in-app currency for AI features. 1 Zencredit ≈ ₹1
            retail. Plan subscriptions grant a monthly bundle (Basic 400 ZC,
            Intermediate 1000 ZC, Advanced 2500 ZC) and you can top up at any
            time at <Link to="/account/topup" className="text-emerald-400 hover:underline">/account/topup</Link>.
          </p>
          <p>The features and what they cost:</p>
          <ul className="list-disc list-inside space-y-1">
            <li>
              <strong className="text-text-primary">AI Tag a match — 15 ZC.</strong>{" "}
              On the matches list (per row), in the BBB action bar, on the
              QC screen, or from the Scorecard. Fills missing stroke /
              shot connection / outcome on every ball that needs it. Best
              after you've finished scoring or if you've left some chips
              empty mid-innings.
            </li>
            <li>
              <strong className="text-text-primary">AI Suggest a ball — 7 ZC.</strong>{" "}
              On the BBB action bar (closed beta — coming to all paid plans
              soon). Sends ~8 video frames around the current ball to Claude;
              fills the entire accordion with a single click. Designed for
              when you have a video of the match.
            </li>
            <li>
              <strong className="text-text-primary">AI match report — 30 ZC.</strong>{" "}
              On the Scorecard page. Generates a narrative summary — runs,
              turning points, MOM nomination — usable for press kits, social
              posts, club newsletters.
            </li>
            <li>
              <strong className="text-text-primary">Regenerate ball commentary — 3 ZC.</strong>{" "}
              On the per-ball edit dialog. Refreshes the auto-commentary for
              one delivery if the default reads stiff.
            </li>
            <li>
              <strong className="text-text-primary">Scorer chat — 2 ZC per message.</strong>{" "}
              Bottom-right floating chat assistant on the BBB screen. Voice
              + text. Can undo, edit, look up rules, debug match state.
            </li>
          </ul>
          <p className="mt-3">
            <strong className="text-text-primary">Soft signals you'll see:</strong>{" "}
            wallet pill in the top-right corner of every key screen, "AI Tag · 15 ZC"
            buttons inline, low-balance toast when you drop below 20 ZC, and a
            blocking modal if you try a feature with insufficient balance —
            that one routes you to the topup page.
          </p>
        </Section>

        <div className="mt-10 bg-bg-primary border border-bg-secondary rounded-xl p-5">
          <p className="text-sm text-text-secondary">
            For technical issues, contact{" "}
            <a href="mailto:a.manivannan@sportingzenminds.com" className="text-emerald-400 hover:underline">
              admin
            </a>.
          </p>
        </div>
      </div>
    </div>
  );
}
